import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchBenefitOverrides,
  postBenefitOverride,
  postBenefitOverrideApply,
  postBenefitOverrideDecision,
} from './api';
import type {
  ApplyBenefitOverrideResult,
  BenefitOverride,
  ProposeBenefitOverrideInput,
} from './types';

// #819 (SKY PoC A9) — event-level benefit override hooks. Kept in their own
// module (not `use-disruptions.ts`) so the desk tests that mock that module
// keep working untouched; the panel is stubbed where the tab is tested.

export function benefitOverridesKey(urn: string): readonly unknown[] {
  return ['disruptions', urn, 'benefit-overrides'] as const;
}

/** The disruption's overrides, newest first. Re-read after every write (small list). */
export function useBenefitOverrides(urn: string | undefined, enabled = true) {
  return useQuery<BenefitOverride[]>({
    queryKey: ['disruptions', urn, 'benefit-overrides'],
    queryFn: () => fetchBenefitOverrides(urn!),
    enabled: !!urn && enabled,
    staleTime: 15_000,
    retry: false,
  });
}

function afterOverrideWrite(qc: ReturnType<typeof useQueryClient>, urn: string, moneyMoved: boolean): void {
  void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'benefit-overrides'] });
  if (moneyMoved) {
    // The apply touched cards (top-ups) and pending allowances (cases denorm):
    // the board re-reads now, the roster denorm lands over the bus (~4 s bridge).
    const invalidate = (): void => {
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'care'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
    };
    invalidate();
    setTimeout(invalidate, 4000);
  }
}

/** "Elevar monto" — creates a PROPOSED record (nothing moves until authorized + applied). */
export function useProposeBenefitOverride(urn: string) {
  const qc = useQueryClient();
  return useMutation<BenefitOverride, Error, ProposeBenefitOverrideInput>({
    mutationFn: (body) => postBenefitOverride(urn, body),
    onSuccess: () => afterOverrideWrite(qc, urn, false),
  });
}

/** Aprobar / Rechazar — supervisor only; cases refuses the author (409 same_actor). */
export function useDecideBenefitOverride(urn: string) {
  const qc = useQueryClient();
  return useMutation<BenefitOverride, Error, { overrideUrn: string; decision: 'APPROVE' | 'REJECT'; note?: string }>({
    mutationFn: ({ overrideUrn, decision, note }) =>
      postBenefitOverrideDecision(urn, overrideUrn, { decision, ...(note ? { note } : {}) }),
    onSuccess: () => afterOverrideWrite(qc, urn, false),
  });
}

/** Aplicar — one call for the whole event; idempotent. */
export function useApplyBenefitOverride(urn: string) {
  const qc = useQueryClient();
  return useMutation<ApplyBenefitOverrideResult, Error, { overrideUrn: string }>({
    mutationFn: ({ overrideUrn }) => postBenefitOverrideApply(urn, overrideUrn),
    onSuccess: () => afterOverrideWrite(qc, urn, true),
  });
}
